import type { TextInputProps } from '../TextInput/index.ts';
import { Autocomplete as BaseUiAutocomplete } from '@base-ui/react/autocomplete';
import { mergeProps } from '@base-ui/react/merge-props';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { TextInput } from '../TextInput/index.ts';

export type AutocompleteProps<ItemValue> = Pick<
  BaseUiAutocomplete.Root.Props<ItemValue>,
  | 'children'
  | 'disabled'
  | 'inline'
  | 'itemToStringValue'
  | 'modal'
  | 'name'
  | 'onItemHighlighted'
  | 'onOpenChange'
  | 'onOpenChangeComplete'
  | 'onValueChange'
  | 'open'
  | 'openOnInputClick'
  | 'value'
  | 'virtualized'
> & {
  items?: readonly ItemValue[] | undefined;
};

export function Autocomplete<ItemValue>(props: AutocompleteProps<ItemValue>) {
  const { locale } = useIntl();
  return <BaseUiAutocomplete.Root {...props} locale={locale} mode="none" />;
}

export type AutocompleteInputProps =
  | Omit<TextInputProps, 'ref' | 'value'>
  | { render: NonNullable<BaseUiAutocomplete.Input.Props['render']> };

export function AutocompleteInput(props: AutocompleteInputProps) {
  if ('render' in props) {
    return (
      <BaseUiAutocomplete.InputGroup>
        <BaseUiAutocomplete.Input render={props.render} />
      </BaseUiAutocomplete.InputGroup>
    );
  }

  return (
    <BaseUiAutocomplete.InputGroup>
      <BaseUiAutocomplete.Input
        render={renderProps => <TextInput {...mergeProps(renderProps, props)} />}
      />
    </BaseUiAutocomplete.InputGroup>
  );
}

export type AutocompletePopupProps = React.PropsWithChildren<
  Pick<
    BaseUiAutocomplete.Positioner.Props,
    'align' | 'alignOffset' | 'positionMethod' | 'side' | 'sideOffset'
  > & {
    render?: BaseUiAutocomplete.Popup.Props['render'];
  }
>;

export function AutocompletePopup({
  align = 'center',
  alignOffset,
  children,
  positionMethod = 'absolute',
  render,
  side = 'bottom',
  sideOffset = 4
}: AutocompletePopupProps) {
  const defaultRender: BaseUiAutocomplete.Popup.Props['render'] = renderProps => (
    <div
      {...renderProps}
      className={clsx(
        'max-h-[min(var(--available-height),22rem)] w-(--anchor-width) max-w-(--available-width) scroll-pt-1 scroll-pb-1 overflow-y-auto overscroll-contain py-1',
        'rounded-md bg-white text-sm shadow-lg ring-1 ring-black/5'
      )}
    />
  );

  return (
    <BaseUiAutocomplete.Portal className="relative z-50">
      <BaseUiAutocomplete.Positioner
        align={align}
        alignOffset={alignOffset}
        className="outline-hidden"
        positionMethod={positionMethod}
        side={side}
        sideOffset={sideOffset}
      >
        <BaseUiAutocomplete.Popup render={render || defaultRender}>
          {children}
        </BaseUiAutocomplete.Popup>
      </BaseUiAutocomplete.Positioner>
    </BaseUiAutocomplete.Portal>
  );
}

export const AutocompleteList = BaseUiAutocomplete.List;

export function AutocompleteListItem(
  props: Pick<
    BaseUiAutocomplete.Item.Props,
    'children' | 'disabled' | 'onClick' | 'index' | 'render' | 'value'
  >
) {
  const render: BaseUiAutocomplete.Item.Props['render'] = renderProps => (
    <div
      {...renderProps}
      className={clsx(
        'group cursor-default px-3 py-2 text-sm outline-hidden select-none',
        'data-highlighted:relative data-highlighted:z-0 data-highlighted:text-white data-highlighted:before:absolute data-highlighted:before:inset-x-0 data-highlighted:before:inset-y-0 data-highlighted:before:z-[-1] data-highlighted:before:bg-primary-600',
        'not-data-disabled:text-gray-900 data-disabled:text-gray-400'
      )}
    />
  );

  return <BaseUiAutocomplete.Item {...props} render={props.render || render} />;
}

export const AutocompleteEmpty = BaseUiAutocomplete.Empty;

export const AutocompleteBackdrop = BaseUiAutocomplete.Backdrop;
